'use client';

import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  useDrawer,
  OrderStatusCheckboxList,
  useBreakpoint,
  Conditional,
  PreferenceLink
} from '@cat-ecom/pcc-components';

import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { CatHeading } from 'blocks-nextjs/blocks-react/components/Heading';
import { CatIconArrowBack } from 'blocks-nextjs/blocks-react/components/Icons/ArrowBack';
import { useTranslations } from 'next-intl';
import { ViewResultsButton } from '../common/view-results-button';
import useOrderStatusList from 'components/notifications/hooks/use-order-status-list';
import { useFormContext } from 'react-hook-form';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { NOTIFICATION_PREFERENCES_URL } from 'constants/links';
import { useParams } from 'next/navigation';
import { getSeoUrl } from 'utils/links';
import { ClearFiltersButton } from '../common/clear-filters-button';

export const FilterByOrderStatus = ({
  orderStatusDrawer,
  notificationsCount,
  filterDrawer,
  userAttributes,
  calRoles = [],
  isPCCQuoteNotificationsFilteringFlag
}: {
  orderStatusDrawer: ReturnType<typeof useDrawer>;
  notificationsCount: number;
  filterDrawer: ReturnType<typeof useDrawer>;
  userAttributes: Array<{ name: string; value: string }>;
  calRoles?: string[];
  isPCCQuoteNotificationsFilteringFlag?: boolean;
}) => {
  const t = useTranslations();
  const params = useParams();
  const esiteUrl = getSeoUrl(params);

  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const { watch, setValue } = useFormContext();
  const filterByOrderStatuses = watch('filterByOrderStatuses');

  const orderStatusList = useOrderStatusList({
    userAttributes,
    calRoles,
    isPCCQuoteNotificationsFilteringFlag
  });

  const closeDrawers = () => {
    orderStatusDrawer.closeDrawer();
    filterDrawer.closeDrawer();
  };

  const handleCheckbox = (e, value) => {
    const updatedOrderStatus = e.target.checked
      ? [
          ...filterByOrderStatuses,
          { orderStatus: value.value, label: value.label }
        ]
      : filterByOrderStatuses?.filter(
          (data) => data.orderStatus !== value.value
        );
    setValue('filterByOrderStatuses', updatedOrderStatus);
  };

  const handleClearFilterBtn = () => {
    setValue('filterByOrderStatuses', []);
  };

  return (
    <Drawer {...orderStatusDrawer.drawerProps} onBlDrawerClose={closeDrawers}>
      <DrawerHeader className={'d-flex align-items-center text-capitalize'}>
        <CatButton
          className="me-2"
          hideText
          onClick={orderStatusDrawer.closeDrawer}
          variant="ghost"
        >
          <CatIconArrowBack slot="before" />
        </CatButton>
        <CatHeading
          variant="title"
          slot="header"
          className="cat-u-theme-typography-title-sm"
        >
          {t('FILTER_BY_ORDER_STATUS')}
        </CatHeading>
      </DrawerHeader>
      <DrawerBody className="p-3 ps-4">
        <PreferenceLink
          text={t('NOTIF_PREF_FILTERING_MESSAGE')}
          link={`${esiteUrl}${NOTIFICATION_PREFERENCES_URL}`}
          linkText={t('NOTIF_PREF_HYPERLINK')}
          className="mt-1 mb-3 cat-u-theme-typography-footnote"
        />
        <OrderStatusCheckboxList
          orderStatusList={orderStatusList}
          handlecheckbox={handleCheckbox}
          orderStatusValue={filterByOrderStatuses}
        />
      </DrawerBody>
      <DrawerFooter className="px-2">
        <ViewResultsButton
          onClick={closeDrawers}
          count={notificationsCount}
          fullWidth={!isMD}
        />
        <Conditional test={filterByOrderStatuses.length}>
          <ClearFiltersButton
            onClick={handleClearFilterBtn}
            fullWidth={!isMD}
          />
        </Conditional>
      </DrawerFooter>
    </Drawer>
  );
};
